import React, { useState, useEffect } from 'react';
import { UserProfile } from '../../domain/types/index.ts';
import Onboarding from '../../../profile-onboarding/entry-points/pages/Onboarding.tsx';
import Dashboard from '../../entry-points/pages/Dashboard.tsx';
import Sidebar from '../../entry-points/api/Sidebar.tsx';
import ChatWidget from '../../entry-points/api/ChatWidget.tsx';
import CommunityHub from '../../../community-hub/entry-points/pages/CommunityHub.tsx';
import FacilityLocator from '../../../facility-locator/entry-points/pages/FacilityLocator.tsx';
import MessagingSystem from '../../../messaging-system/entry-points/pages/MessagingSystem.tsx';
import Gamification from '../../../gamification/entry-points/components/Gamification.tsx';
import MealPlanner from '../../../meal-planning/entry-points/pages/MealPlanner.tsx';
import ProfessionalServices from '../../../professional-services/entry-points/pages/ProfessionalServices.tsx';
import AdminDashboard from '../../../admin-dashboard/entry-points/pages/AdminDashboard.tsx';
import GroceryIntegration from '../../../grocery-integration/entry-points/pages/GroceryIntegration.tsx';
import RestaurantPartnership from '../../../restaurant-partnership/entry-points/pages/RestaurantPartnership.tsx';
import AnalyticsReporting from '../../../analytics-reporting/entry-points/pages/AnalyticsReporting.tsx';
import HelpSupport from '../../../help-support/entry-points/pages/HelpSupport.tsx';
import PaymentPortal from '../../../payments/entry-points/pages/PaymentPortal.tsx';
import { LayoutDashboard, Users, MapPin, MessageSquare, Trophy, Menu, X, Coffee, Calendar, Settings, LogOut, CreditCard, Shield, User, Flame, Bell, Search } from 'lucide-react';

const App: React.FC = () => {
  const [userProfile, setUserProfile] = useState<UserProfile | null>(null);
  const [activeTab, setActiveTab] = useState('dashboard');
  const [isMobileMenuOpen, setIsMobileMenuOpen] = useState(false);

  // Theme State
  const [theme, setTheme] = useState<'light' | 'dark'>('light');

  // Toggle Theme Function
  const toggleTheme = () => {
    setTheme(prev => prev === 'light' ? 'dark' : 'light');
  };

  // Apply theme to body/html
  useEffect(() => {
    const root = window.document.documentElement;
    if (theme === 'dark') {
        root.classList.add('dark');
    } else {
        root.classList.remove('dark');
    }
  }, [theme]);

  if (!userProfile) {
    return <Onboarding onComplete={setUserProfile} />;
  }

  const renderContent = () => {
    switch(activeTab) {
      case 'dashboard':
        return <Dashboard user={userProfile} />;
      case 'progress':
        return <AnalyticsReporting user={userProfile} />;
      case 'messages':
        return <MessagingSystem user={userProfile} />;
      case 'community':
        return <CommunityHub user={userProfile} />;
      case 'facilities':
        return <FacilityLocator user={userProfile} />;
      case 'achievements':
        return <Gamification user={userProfile} />;
      case 'meal-plans':
        return <MealPlanner user={userProfile} />;
      case 'grocery':
        return <GroceryIntegration user={userProfile} />;
      case 'dining':
        return <RestaurantPartnership user={userProfile} />;
      case 'appointments':
        return <ProfessionalServices user={userProfile} />;
      case 'payments':
        return <PaymentPortal user={userProfile} />;
      case 'support':
        return <HelpSupport user={userProfile} />;
      case 'admin':
        return <AdminDashboard />;
      case 'profile':
        return <AnalyticsReporting user={userProfile} />; // Reuse analytics for profile tab on mobile for now
      default:
        return <Dashboard user={userProfile} />;
    }
  };

  const handleTabChange = (tab: string) => {
    setActiveTab(tab);
    setIsMobileMenuOpen(false);
  };

  // Global Header Component
  const GlobalHeader = () => (
    <header className="h-16 border-b border-stone-200 dark:border-stone-800 bg-white/80 dark:bg-stone-900/80 backdrop-blur-md flex items-center justify-between px-4 md:px-8 sticky top-0 z-30 shrink-0 transition-colors">
        <div className="flex items-center gap-4">
            {/* Mobile Only: Menu Trigger (Left aligned on mobile) */}
            <div className="md:hidden">
                 <button
                    onClick={() => setIsMobileMenuOpen(true)}
                    className="p-2 -ml-2 rounded-xl text-gray-500 dark:text-gray-400"
                >
                    <Menu size={24} />
                </button>
            </div>

            {/* Logo (Hidden on Desktop as Sidebar has it) */}
            <div className="md:hidden flex items-center">
                <div className="w-8 h-8 bg-gradient-to-tr from-health-600 to-health-400 rounded-lg shadow-neon flex items-center justify-center text-white font-bold text-sm mr-2">W</div>
                <span className="font-bold text-gray-900 dark:text-white">Well Being</span>
            </div>

            {/* Desktop: Page Title / Breadcrumb Placeholder */}
            <div className="hidden md:block">
                 <h2 className="text-lg font-medium text-gray-800 dark:text-white capitalize">{activeTab.replace('-', ' ')}</h2>
            </div>
        </div>

        {/* Right Actions: Gamification & Profile */}
        <div className="flex items-center gap-3 md:gap-6">

            {/* Gamification Stats (Visible on both Mobile & Desktop) */}
            <div className="flex items-center gap-3 bg-stone-100 dark:bg-stone-800 pl-4 pr-1 py-1 rounded-full border border-stone-200 dark:border-stone-700">
                <div className="flex flex-col items-end">
                    <span className="text-[10px] uppercase font-bold text-gray-400 dark:text-gray-500 leading-none">Level {userProfile.currentLevel}</span>
                    <span className="text-xs font-bold text-gray-900 dark:text-white leading-tight">{userProfile.communityRank}</span>
                </div>
                <div className="w-8 h-8 rounded-full bg-gradient-to-br from-yellow-300 to-orange-500 flex items-center justify-center text-white font-bold text-xs shadow-sm ring-2 ring-white dark:ring-stone-900">
                    <Trophy size={14} />
                </div>
            </div>

            <div className="hidden md:flex items-center gap-4">
                 <button className="p-2 text-gray-400 hover:text-gray-600 dark:hover:text-gray-200 transition-colors relative">
                    <Bell size={20} />
                    <span className="absolute top-1.5 right-2 w-2 h-2 bg-red-500 rounded-full border-2 border-white dark:border-stone-900"></span>
                 </button>
                 <div className="w-px h-8 bg-stone-200 dark:bg-stone-700"></div>
            </div>

            {/* Profile Avatar */}
            <button onClick={() => handleTabChange('profile')} className="w-9 h-9 rounded-full bg-stone-200 dark:bg-stone-700 overflow-hidden border border-white dark:border-stone-600 shadow-sm transition-transform hover:scale-105 active:scale-95">
                 <div className="w-full h-full flex items-center justify-center text-stone-500 dark:text-stone-300 text-xs font-bold bg-gradient-to-br from-stone-100 to-stone-200 dark:from-stone-700 dark:to-stone-600">
                    {userProfile.name.charAt(0)}
                 </div>
            </button>
        </div>
    </header>
  );

  return (
    <div className="flex h-screen w-full bg-[#FAFAFA] dark:bg-[#0f0f0f] font-sans text-gray-800 dark:text-white overflow-hidden transition-colors duration-300">
      <Sidebar activeTab={activeTab} setActiveTab={setActiveTab} theme={theme} toggleTheme={toggleTheme} />

      <main className="flex-1 h-full relative overflow-hidden flex flex-col w-full">
        {/* Global Header for Mobile & Desktop */}
        <GlobalHeader />

        {/* Content Area - Full Width on Desktop */}
        <div className="flex-1 overflow-hidden relative pb-20 md:pb-0 w-full">
            {renderContent()}
        </div>

        <ChatWidget user={userProfile} />

        {/* Mobile Bottom Navigation (5 Core Tabs) */}
        <div className="md:hidden fixed bottom-0 left-0 right-0 h-[84px] bg-white/95 dark:bg-stone-900/95 backdrop-blur-xl border-t border-stone-200 dark:border-stone-800 z-40 flex justify-between items-center px-2 pb-5 shadow-[0_-4px_20px_rgba(0,0,0,0.03)] dark:shadow-[0_-4px_20px_rgba(0,0,0,0.3)] transition-colors">
            <button
                onClick={() => handleTabChange('dashboard')}
                className={`flex flex-col items-center gap-1 p-2 flex-1 transition-colors ${activeTab === 'dashboard' ? 'text-health-600 dark:text-health-400' : 'text-gray-400 dark:text-gray-500'}`}
            >
                <LayoutDashboard size={22} strokeWidth={activeTab === 'dashboard' ? 2.5 : 2} />
                <span className="text-[10px] font-medium">Home</span>
            </button>
            <button
                onClick={() => handleTabChange('meal-plans')}
                className={`flex flex-col items-center gap-1 p-2 flex-1 transition-colors ${activeTab === 'meal-plans' ? 'text-health-600 dark:text-health-400' : 'text-gray-400 dark:text-gray-500'}`}
            >
                <Coffee size={22} strokeWidth={activeTab === 'meal-plans' ? 2.5 : 2} />
                <span className="text-[10px] font-medium">Meals</span>
            </button>

            {/* Center: Community Hub */}
            <div className="relative -top-5 flex-1 flex justify-center">
                <button
                    onClick={() => handleTabChange('community')}
                    className={`w-14 h-14 rounded-full flex items-center justify-center shadow-lg transition-all ${
                        activeTab === 'community' 
                        ? 'bg-health-600 text-white ring-4 ring-health-100 dark:ring-health-900 scale-110' 
                        : 'bg-gray-900 dark:bg-white text-white dark:text-gray-900'
                    }`}
                >
                    <Users size={24} />
                </button>
            </div>

            <button
                onClick={() => handleTabChange('facilities')}
                className={`flex flex-col items-center gap-1 p-2 flex-1 transition-colors ${activeTab === 'facilities' ? 'text-health-600 dark:text-health-400' : 'text-gray-400 dark:text-gray-500'}`}
            >
                <MapPin size={22} strokeWidth={activeTab === 'facilities' ? 2.5 : 2} />
                <span className="text-[10px] font-medium">Explore</span>
            </button>
            <button
                onClick={() => handleTabChange('profile')}
                className={`flex flex-col items-center gap-1 p-2 flex-1 transition-colors ${activeTab === 'profile' ? 'text-health-600 dark:text-health-400' : 'text-gray-400 dark:text-gray-500'}`}
            >
                <User size={22} strokeWidth={activeTab === 'profile' ? 2.5 : 2} />
                <span className="text-[10px] font-medium">Me</span>
            </button>
        </div>

        {/* Mobile Full Menu Overlay (For items not in bottom nav) */}
        {isMobileMenuOpen && (
            <div className="fixed inset-0 z-50 bg-[#FAFAFA] dark:bg-[#0f0f0f] flex flex-col md:hidden animate-fade-in-up transition-colors">
                <div className="p-6 flex justify-between items-center border-b border-stone-100 dark:border-stone-800 bg-white dark:bg-stone-900">
                    <h2 className="text-xl font-bold text-gray-900 dark:text-white">More</h2>
                    <button
                        onClick={() => setIsMobileMenuOpen(false)}
                        className="p-2 bg-stone-100 dark:bg-stone-800 rounded-full text-gray-600 dark:text-gray-300 hover:bg-stone-200 dark:hover:bg-stone-700"
                    >
                        <X size={24} />
                    </button>
                </div>

                <div className="flex-1 overflow-y-auto p-6 space-y-2">
                    <p className="text-xs font-bold text-gray-400 uppercase tracking-wider mb-4 ml-2">All Modules</p>

                    {[
                        { id: 'messages', label: 'Messages', icon: MessageSquare, color: 'text-blue-600 dark:text-blue-400', bg: 'bg-blue-50 dark:bg-blue-900/20' },
                        { id: 'achievements', label: 'Achievements', icon: Trophy, color: 'text-yellow-600 dark:text-yellow-400', bg: 'bg-yellow-50 dark:bg-yellow-900/20' },
                        { id: 'appointments', label: 'Professional Help', icon: Calendar, color: 'text-purple-600 dark:text-purple-400', bg: 'bg-purple-50 dark:bg-purple-900/20' },
                        { id: 'payments', label: 'Payments', icon: CreditCard, color: 'text-green-600 dark:text-green-400', bg: 'bg-green-50 dark:bg-green-900/20' },
                        { id: 'admin', label: 'Admin Panel', icon: Shield, color: 'text-red-600 dark:text-red-400', bg: 'bg-red-50 dark:bg-red-900/20' },
                    ].map((item) => {
                        const Icon = item.icon;
                        return (
                            <button
                                key={item.id}
                                onClick={() => handleTabChange(item.id)}
                                className={`w-full flex items-center gap-4 p-4 rounded-2xl border transition-all ${
                                    activeTab === item.id 
                                    ? 'bg-white dark:bg-stone-800 border-health-500 shadow-md' 
                                    : 'bg-white dark:bg-stone-800 border-stone-100 dark:border-stone-700 shadow-sm'
                                }`}
                            >
                                <div className={`p-3 rounded-xl ${item.bg} ${item.color}`}>
                                    <Icon size={24} />
                                </div>
                                <span className="font-medium text-lg text-gray-800 dark:text-white">{item.label}</span>
                            </button>
                        );
                    })}

                    <div className="my-6 border-t border-stone-200 dark:border-stone-800"></div>

                    <button
                        onClick={toggleTheme}
                        className="w-full flex items-center gap-4 p-4 text-gray-500 dark:text-gray-400 font-medium hover:bg-stone-50 dark:hover:bg-stone-800 rounded-2xl transition-colors"
                    >
                        {theme === 'light' ? <Settings size={20} /> : <Settings size={20} />}
                        {theme === 'light' ? 'Switch to Dark Mode' : 'Switch to Light Mode'}
                    </button>
                    <button className="w-full flex items-center gap-4 p-4 text-red-500 font-medium hover:bg-red-50 dark:hover:bg-red-900/10 rounded-2xl transition-colors">
                        <LogOut size={20} /> Sign Out
                    </button>
                </div>
            </div>
        )}

      </main>
    </div>
  );
};

export default App;